
/**
 * Service for sharing BJJ techniques
 */
import { supabase } from '@/integrations/supabase/client';
import { Technique } from '@/types/technique';
import { generateShareId } from './shareIdGenerator';
import { generateShareableUrl as generateUrl } from './referralService';



/**
 * Creates a shared version of a technique that can be accessed by others
 * @param technique The technique to share
 * @param referralCode Optional referral code to track the share source
 * @returns The unique share ID for the created shared technique
 */
export async function createSharedTechnique(technique: Technique, referralCode?: string): Promise<string | null> {
  try {
    if (!technique || !technique.id) {
      throw new Error("Invalid technique data");
    }

    console.log("Creating shared technique for:", technique.id);
    
    // Generate a unique share ID
    const shareId = generateShareId();
    
    // Prepare the data for the shared technique
    const sharedTechniqueData = {
      original_id: technique.id,
      share_id: shareId,
      name: technique.name || "Untitled Technique",
      description: technique.description || "",
      imageurl: technique.image_url || null,
      videourl: technique.video_url || null,
      positionid: technique.position_id || null,
      techniquetype: technique.technique_type || null,
      techniqueposition: technique.technique_position || null,
      difficulty: technique.difficulty || null,
      keypoints: technique.key_points || [],
      commonmistakes: technique.common_mistakes || [],
      referral_code: referralCode || null
    };
    
    // Call Supabase to create the shared technique
    const { data, error } = await supabase
      .from('shared_techniques')
      .insert(sharedTechniqueData)
      .select('share_id')
      .single();
    
    if (error) {
      console.error("Error creating shared technique:", error);
      throw new Error(`Failed to create shared technique: ${error.message}`);
    }
    
    if (!data || !data.share_id) {
      console.error("No share ID returned:", data);
      throw new Error("No share ID returned from server");
    }
    
    console.log("Shared technique created successfully with ID:", data.share_id);
    return data.share_id;
  } catch (error: unknown) {
    console.error("Error in createSharedTechnique:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Generates a shareable URL for a technique using the centralized domain utility system
 * @param shareId The unique share ID for the technique
 * @param referralCode Optional referral code to track the share source
 * @returns A complete shareable URL that works across all platforms
 */
export function generateShareableUrl(shareId: string, referralCode?: string): string {
  return generateUrl(shareId, 'technique', referralCode);
}

// Export common sharing functions from our unified services
export { incrementViewCount, incrementImportCount } from './contentStats';
